<template>
  <div>
    <StructureTable
      bulkActions
      :items="items"
      :headers="headers"
      :totalItems="totalSites"
      :loading="loading"
      :title="$t('common.site_group_other')"
      :groups="groups"
      @goToSettings="goToSiteSettings"
      @edit="handleEdit"
      @manageGroups="$emit('manageGroups')"
      @createGroup="$emit('createGroup')"
      @apply="handleGroupSites"
      @getItems="handleGetSites"
      @selectItems="$emit('selectSites', $event)"
    />
    <EditSiteDialog
      :site="selectedSite"
      :sites="sites"
      :visible="showEditDialog"
      :loading="saving"
      :organization="organization"
      @close="handleClose"
      @save="saveSite"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import StructureTable from '../../Shared/StructureTable.vue'

import EditSiteDialog from './EditSiteDialog.vue'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'Sitestab',

  mixins: [mixin],

  components: { StructureTable, EditSiteDialog },

  props: {
    organizationId: String,
    organization: Object,
    loading: Boolean,
    sites: { type: Array, required: true },
    totalSites: { type: Number, required: true },
    groups: Array,
  },

  data() {
    return {
      options: {},
      saving: false,
      fetching: false,
      selectedSite: null,
      showEditDialog: false,
      headers: [
        {
          text: _.capitalize(this.$t('site_settings.site_name')),
          align: 'start',
          sortable: true,
          value: 'name',
          show: true,
          width: '100px',
        },

        {
          text: _.capitalize(this.$t('common.country_one')),
          sortable: false,
          value: 'countryText',
          align: 'start',
          show: true,
          width: '100px',
        },
        {
          text: _.capitalize(this.$t('common.site_group_other')),
          sortable: false,
          value: 'groups',
          align: 'start',
          show: true,
          width: '200px',
        },
        // Check with backend the posibility to add this column
        // {
        //   text: _.capitalize(this.$t('common.tag_other')),
        //   sortable: false,
        //   value: 'tags',
        //   align: 'start',
        //   show: true,
        // },
        {
          text: _.capitalize(this.$t('common.action_other')),
          sortable: false,
          value: 'actions',
          align: 'center',
          show: true,
          width: '80px',
        },
      ],
    }
  },

  computed: {
    ...mapGetters(['account']),

    items() {
      return this.sites.map(site => {
        return {
          ...site,
          countryText: this.$t(`countries.${site.countryCodes[0]}`),
          id: site._id,
          actions: ['edit', 'goToSettings'],
        }
      })
    },
  },

  methods: {
    ...mapActions(['getOrganizationStructure']),

    async getGroup(groupId) {
      const response = await api.sitegroups.getById(
        this.organizationId,
        groupId
      )
      return response.data
    },

    goToSiteSettings(site) {
      const breadcrumbs = [
        {
          href: `/organization-settings/${this.organizationId}`,
          name: this.$t('menu.organization_settings'),
        },
        {
          name: this.$t('common.site_other'),
          to: {
            path: `/organization-structure/${this.organizationId}`,
          },
        },
        {
          href: `/organizations/${this.organizationId}/sites/${site.id}/settings`,
          name: site.name,
        },
      ]

      this.navigateTo({
        name: 'site-settings',
        params: {
          breadcrumbs,
          organizationId: this.organizationId,
          siteId: site.id,
        },
      })
    },

    async handleGroupSites(data) {
      try {
        this.saving = true
        const group = await this.getGroup(data.group.id)

        const sites = _.uniq([...group.sites, ...data.selectedIds])

        if (sites?.length > this.$constants.maxTagsPerGroup)
          return this.showError(
            this.$t('organization_structure.cannot_add_more_than_100_sites')
          )

        await api.sitegroups.editSites(this.organizationId, data.group.id, {
          sites,
        })
        if (this.account?.organization?._id === this.organizationId) {
          await this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.$emit('getSites', this.options)
        this.$emit('getSiteGroups')
        this.showSuccess(
          this.$t('organization_structure.sites_group_updated_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.saving = false
      }
    },

    handleGetSites(options) {
      this.options = options
      this.$emit('getSites', options)
    },

    async saveSite(site) {
      this.saving = true
      try {
        await api.site.updateOrganizationSite(
          this.organizationId,
          site.id,
          site
        )
        if (this.account?.organization?._id === this.organizationId) {
          await this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.$emit('getSites', this.options)
        this.$emit('cleanSearchTerm')
        this.selectedSite = null
        this.showEditDialog = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.saving = false
      }
    },

    handleClose() {
      this.selectedSite = null
      this.showEditDialog = false
    },

    handleEdit(site) {
      this.selectedSite = site
      this.showEditDialog = true
    },
  },
}
</script>
